// Helper function to parse error messages
export const parseError = error => {
  if (!error) return 'An unknown error occurred';

  const errorMessage = error.message || error.toString();
  const errorCode = error.code;
  const normalizedErrorMessage = String(errorMessage).toLowerCase();
  const simulationLogs = Array.isArray(error?.logs)
    ? error.logs
    : Array.isArray(error?.simulationResponse?.logs)
      ? error.simulationResponse.logs
      : [];

  // User rejected transaction
  if (
    errorMessage.includes('user rejected') ||
    errorMessage.includes('User denied') ||
    errorMessage.includes('user rejected the request') ||
    errorCode === 4001 ||
    errorCode === 'ACTION_REJECTED'
  ) {
    return 'Transaction was rejected. Please try again.';
  }

  // Solana wallet selection
  if (
    normalizedErrorMessage.includes('walletnotselectederror') ||
    normalizedErrorMessage.includes('wallet not selected') ||
    normalizedErrorMessage.includes('no wallet selected') ||
    normalizedErrorMessage.includes('please select a solana wallet first')
  ) {
    return 'Select a Solana wallet to continue.';
  }

  // Solana simulation / preflight failures
  if (normalizedErrorMessage.includes('accountnotfound')) {
    if (normalizedErrorMessage.includes('simulation failed on solana:')) {
      return `${errorMessage}. This usually means one of the required program accounts is missing on the selected RPC.`;
    }
    return 'Required Solana token account was not found. Create/fund the associated token accounts and try again.';
  }

  if (normalizedErrorMessage.includes('treasury stablecoin account not found')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('treasury usdn account not found')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('input mint not found on this network')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('usdn mint not found on this network')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('configured swap program is not deployed or executable')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('has wrong owner program')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('connected wallet account does not exist on this rpc yet')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('connected wallet has 0 sol for fees on this network')) {
    return errorMessage;
  }
  if (normalizedErrorMessage.includes('input token account not found for selected token mint')) {
    return errorMessage;
  }

  if (
    normalizedErrorMessage.includes('simulation failed') ||
    normalizedErrorMessage.includes('failed to simulate transaction') ||
    normalizedErrorMessage.includes('transaction simulation failed') ||
    normalizedErrorMessage.includes('preflight')
  ) {
    if (normalizedErrorMessage.includes('simulation failed on solana:')) {
      if (normalizedErrorMessage.includes('custom(3007)')) {
        return `${errorMessage}. This indicates account owner mismatch (Custom 3007).`;
      }
      if (normalizedErrorMessage.includes('signature verification failed')) {
        return `${errorMessage}. Treasury-swap currently requires a treasury wallet co-signature, so direct end-user signing is not sufficient.`;
      }
      return errorMessage;
    }
    if (simulationLogs.length) {
      const bestLog = simulationLogs
        .slice()
        .reverse()
        .find(line => line.includes('Program log:') || line.includes('Program failed:'));
      if (bestLog) return `Simulation failed: ${bestLog.replace(/^.*Program log:\s*/, '')}`;
    }
    return 'Simulation failed on Solana. Please verify token accounts, balances, and program configuration.';
  }

  if (normalizedErrorMessage.includes('invalid arguments')) {
    return 'Swap arguments were rejected on Solana. Verify token mints, treasury accounts, and instruction discriminator configuration.';
  }

  // Insufficient balance
  if (
    errorMessage.includes('insufficient funds') ||
    errorMessage.includes('Insufficient') ||
    errorMessage.includes('insufficient balance')
  ) {
    return 'Insufficient balance for this transaction. Please check your balance.';
  }

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('Network') ||
    errorMessage.includes('network error') ||
    errorCode === 'NETWORK_ERROR'
  ) {
    return 'Network error. Please check your connection and try again.';
  }

  // Contract errors - parse specific revert reasons
  if (errorMessage.includes('revert') || errorMessage.includes('execution reverted')) {
    // Try to extract specific error messages
    const revertMatch = errorMessage.match(/revert\s+(.+?)(?:\s|$|")/i);
    if (revertMatch) {
      const reason = revertMatch[1];

      // Map common contract errors to user-friendly messages
      if (reason.includes('InsufficientStablecoinBalance')) {
        return 'Insufficient stablecoin balance. Please check your balance.';
      }
      if (reason.includes('InsufficientStablecoinAllowance')) {
        return 'Insufficient token allowance. Please approve the token first.';
      }
      if (reason.includes('TokenNotSupported')) {
        return 'This token is not supported for swapping.';
      }
      if (reason.includes('ZeroAmount')) {
        return 'Amount must be greater than zero.';
      }

      return `Transaction failed: ${reason}`;
    }

    // Try to extract reason from ethers v6 error info
    if (error.info && error.info.error && error.info.error.message) {
      return `Contract error: ${error.info.error.message}`;
    }

    // Try revert data from error
    if (error.data) {
      return `Transaction reverted (data: ${error.data})`;
    }

    // Show the raw error for debugging rather than a generic message
    const shortened =
      errorMessage.length > 200 ? errorMessage.substring(0, 200) + '...' : errorMessage;
    return `Transaction reverted: ${shortened}`;
  }

  // Rate limiting
  if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests')) {
    return 'Too many requests. Please wait a moment and try again.';
  }

  // Gas estimation errors
  if (errorMessage.includes('gas') || errorMessage.includes('Gas')) {
    return 'Gas estimation failed. Please try again or check your network connection.';
  }

  // Timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
    return 'Request timed out. Please check your connection and try again.';
  }

  // Return shortened error if too long
  if (errorMessage === '[object Object]') {
    return 'Operation failed on Solana. Please retry and check wallet/network configuration.';
  }

  return errorMessage.length > 100 ? 'Transaction failed. Please try again.' : errorMessage;
};
